import invariant from "invariant";
import TableColumnMetaStore from "data/meta/TableColumnMetaStore";
import entityMetaStore from "data/meta/entityMetaStore";
import EntityProxy, {
    hasFilterScript,
    hasIsVersionScript,
} from "data/util/EntityProxy";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import ClientId from "data/ClientId";
import TableMetaStore from "data/meta/TableMetaStore";

const testable = ep => test => {
    const result = test(ep);
    return result == null || !!result;
};

function is(tp, entityInfo) {
    if (entityInfo.type === "CHILD") {
        /*
         * The type of child records is defined by the parent record and the
         * association field; it is not intrinsic to the record itself. So
         * delegate up the tree.
         */
        const pfi = entityFieldMetaStore.getInfo(entityInfo.key, entityInfo.parentPointerKey);
        const pid = tp.getColumn(pfi.logicalKey, true);
        if (pid == null) return false; // the logical tree is a graph, I guess. Woo!
        const pei = entityMetaStore.getInfo(pfi.otherEntityKey);
        return is(new TableProxy(tp.access, pei.tableKey, pid), pei);
    }
    const tester = testable(new EntityProxy(tp, entityInfo.key));
    if (entityInfo.type === "ROOT" && hasIsVersionScript(entityInfo)) {
        if (! tester(entityInfo.scripts.isVersion)) return false;
    }
    if (hasFilterScript(entityInfo)) {
        if (! tester(entityInfo.scripts.filter)) return false;
    }
    return true;
}

/**
 * I proxy Store data as a stateful object w/ a `get` method, like Backbone's
 * Model has, and which is needed for passing into AS scriptlets.
 */
class TableProxy {

    constructor(access, tableKey, id, useKeys = false) {
        if (tableKey == null || id == null) {
            throw new Error(`Proxies cannot be constructed with null keys or ids ('${tableKey}#${id}').`);
        }
        invariant(ClientId.is(id) || typeof id === "number", "Non-numeric, non-client ID '%s' of type %s", id, typeof id);
        if (! access.hasRecord(tableKey, id)) {
            throw new Error(`No ${tableKey}#${id} record is known`);
        }
        this.access = access;
        this.tableKey = tableKey;
        this.id = id;
        this.useKeys = useKeys;
    }

    get(column) {
        try {
            return this.getColumn(column);
        } catch (e) {
            console.warn(this.tableKey + "#" + this.id + "." + column, e);
            return null;
        }
    }

    getAttributeType(column, byKey = this.useKeys) {
        return this._toInfo(column, byKey).type;
    }

    _toInfo(column, byKey) {
        return byKey
            ? TableColumnMetaStore.getInfoByKey(this.tableKey, column)
            : TableColumnMetaStore.getInfoByName(this.tableKey, column);
    }

    getColumn(column, byKey = this.useKeys) {
        const info = this._toInfo(column, byKey);
        if (info.inverse) {
            // todo: this gets combined with `getAllRecords` into something indexed. please.
            const rs = this.access.getAllRecords(info.otherTableKey);
            const invInf = TableColumnMetaStore.getInfoByKey(info.otherTableKey, info.inverseKey);
            const ids = rs.filter(r => {
                const v = r.data[invInf.name];
                return v instanceof Array
                    ? v.indexOf(this.id) >= 0
                    : v === this.id;
            })
                .map(r => r.meta.id);
            return info.multiValued ? ids : ids.length === 0 ? null : ids[0];
        } else {
            return this.access.getColumnData(this.tableKey, this.id, info.name);
        }
    }

    getKey() {
        return this.tableKey + "#" + this.id;
    }

    getRecordMeta() {
        return this.access.getRecordData(this.tableKey, this.id).meta;
    }

    isEntity(entityKeyOrInfo) {
        if (typeof entityKeyOrInfo === "string") {
            entityKeyOrInfo = entityMetaStore.getInfo(entityKeyOrInfo);
        }
        return is(this, entityKeyOrInfo);
    }

    getEntityKeys() {
        const infos = entityMetaStore.getInfosForLogicalTable(this.tableKey);
        const isInfos = infos.filter(info => is(this, info));
        // todo: This isn't quite right. But it's close? Maybe? Barney needs to think about it when he's not 20 minutes away from a 17 day vacation
        // invariant(
        //     isInfos.length > 0 || infos.some(it => it.type !== "CHILD"),
        //     "composed child %s#%s's parent record (via '%s') doesn't exist?", this.tableKey, this.id, infos[0].parentPointerKey);
        return isInfos.map(it => it.key);
    }

    getEntityProxies() {
        return this.getEntityKeys()
            .map(ek => new EntityProxy(this, ek));
    }

    equals(o) {
        if (o == null) return false;
        if (!(o instanceof TableProxy)) return false;
        if (this.tableKey !== o.tableKey) return false;
        if (this.id !== o.id) return false;
        return true;
    }

    canWrite() {
        return TableMetaStore.canWrite(this.tableKey);
    }

    canDelete() {
        return TableMetaStore.canDelete(this.tableKey);
    }

    getMetadata() {
        return this.access.getRecordData(this.tableKey, this.id).meta;
    }

}

export default TableProxy;



// WEBPACK FOOTER //
// ./app/data/util/TableProxy.js